import { Bot, ChevronDown, ChevronUp, Play, RefreshCw, Square } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import {
  CrashingOutExecutionHistory,
  type StepTrace,
} from '@/components/agentflow/CrashingOutExecutionHistory';
import { CoverageCardRedesign } from '@/components/coverage/shared/CoverageCardRedesign';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type CrashingOutPendingReview,
  type CrashingOutStatusResponse,
  useCancelCrashingOutMutation,
  useLazyGetCrashingOutHistoryQuery,
  useLazyGetCrashingOutPendingReviewsQuery,
  useLazyGetCrashingOutRunStatusQuery,
  useLazyGetCrashingOutStatusQuery,
  useStartCrashingOutMutation,
} from '@/services/api/agentflowService';
import { useUpdateCoverageMutation } from '@/services/api/coverageService';
import axiosInstance from '@/services/axiosConfig';
import {
  CoverageCategory,
  type CoverageListItem,
  CoverageStatus,
} from '@/services/types/coverage-types';

interface CoveragePageCrashoutPaneProps {
  matterId: string;
  coverages: CoverageListItem[];
}

interface SchemaProperty {
  type?: string;
  description?: string;
  enum?: string[];
  default?: unknown;
}

const CRASHING_OUT_PROGRESS_MESSAGES = [
  'Discombobulating',
  'Vibe-checking coverage',
  'Summoning adjuster energy',
  'Wrangling task chaos',
  'Politely panicking',
  'Speedrunning bureaucracy',
  'Rizzing up the workflow',
  'Untangling claimant spaghetti',
];
const CRASHING_OUT_PRIMARY_GIF = '/elmo-on-fire.gif';
const CRASHING_OUT_FALLBACK_GIF = '/progress.gif';

const statusColor = (status?: string) => {
  switch (status) {
    case 'running':
      return 'bg-blue-100 text-blue-700';
    case 'completed':
      return 'bg-emerald-100 text-emerald-700';
    case 'failed':
    case 'error':
      return 'bg-red-100 text-red-700';
    case 'canceled':
    case 'terminated':
      return 'bg-zinc-200 text-zinc-700';
    default:
      return 'bg-zinc-100 text-zinc-600';
  }
};

const formatRunStartTime = (run: {
  start_time?: string | null;
  started_at?: string | null;
  startTime?: string | null;
  startedAt?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
}) => {
  const startedAt =
    run.start_time ??
    run.started_at ??
    run.startTime ??
    run.startedAt ??
    run.created_at ??
    run.createdAt;
  if (!startedAt) return '—';

  const parsedDate = new Date(startedAt);
  if (!Number.isNaN(parsedDate.getTime())) return parsedDate.toLocaleString();

  // Handle non-ISO strings and high-precision timestamps from older payloads.
  const normalized = startedAt
    .replace(' ', 'T')
    .replace(/(\.\d{3})\d+/, '$1');
  const reparsedDate = new Date(normalized);
  if (!Number.isNaN(reparsedDate.getTime())) return reparsedDate.toLocaleString();

  return startedAt;
};

const getDefaultFormData = (schema: Record<string, unknown> | null | undefined) => {
  const defaults: Record<string, unknown> = {};
  const properties = (schema?.properties || {}) as Record<string, SchemaProperty>;
  Object.entries(properties).forEach(([key, property]) => {
    if (property.default !== undefined) {
      defaults[key] = property.default;
    } else if (property.type === 'boolean') {
      defaults[key] = false;
    } else {
      defaults[key] = '';
    }
  });
  return defaults;
};

const COVERAGE_TYPE_ORDER: string[] = [
  CoverageCategory.FIRST_PARTY,
  CoverageCategory.THIRD_PARTY,
  CoverageCategory.FIRST_PARTY_HEALTH,
  CoverageCategory.PROPERTY,
  CoverageCategory.WORKERS_COMPENSATION,
  CoverageCategory.NOT_LIABLE_INVOLVED_PARTY,
  CoverageCategory.UNCLEAR,
  'UNSPECIFIED',
];

const getCoverageTypeLabel = (category: CoverageCategory | null | undefined): string => {
  switch (category) {
    case CoverageCategory.FIRST_PARTY:
      return '1P Coverage';
    case CoverageCategory.THIRD_PARTY:
      return '3P Coverage';
    case CoverageCategory.FIRST_PARTY_HEALTH:
      return '1P Health Coverage';
    case CoverageCategory.PROPERTY:
      return 'Property Coverage';
    case CoverageCategory.WORKERS_COMPENSATION:
      return "Workers' Compensation";
    case CoverageCategory.NOT_LIABLE_INVOLVED_PARTY:
      return 'Not Liable Involved Party';
    case CoverageCategory.UNCLEAR:
      return 'Unclear Coverage';
    default:
      return 'Unspecified Coverage';
  }
};

export const CoveragePageCrashoutPane: React.FC<CoveragePageCrashoutPaneProps> = ({
  matterId,
  coverages,
}) => {
  const eligibleCoverages = React.useMemo(
    () =>
      coverages.filter(
        (c) => c.status !== CoverageStatus.REJECTED && c.status !== CoverageStatus.DUPLICATE,
      ),
    [coverages],
  );

  const groupedCoverages = React.useMemo(() => {
    const groups = new Map<string, { label: string; coverages: typeof eligibleCoverages }>();

    eligibleCoverages.forEach((coverage) => {
      const key = coverage.category || 'UNSPECIFIED';
      if (!groups.has(key)) {
        groups.set(key, {
          label: getCoverageTypeLabel(coverage.category),
          coverages: [],
        });
      }
      groups.get(key)!.coverages.push(coverage);
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        const aIndex = COVERAGE_TYPE_ORDER.indexOf(a);
        const bIndex = COVERAGE_TYPE_ORDER.indexOf(b);
        const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        return normalizedA - normalizedB;
      })
      .map(([key, value]) => ({
        key,
        label: value.label,
        coverages: value.coverages,
      }));
  }, [eligibleCoverages]);

  const [expandedCoverageId, setExpandedCoverageId] = React.useState<string | null>(null);
  const [statusByCoverage, setStatusByCoverage] = React.useState<
    Record<string, CrashingOutStatusResponse | null>
  >({});
  const [historyByCoverage, setHistoryByCoverage] = React.useState<
    Record<
      string,
      Array<{
        workflow_id: string;
        run_id: string;
        status: string;
        start_time?: string | null;
        started_at?: string | null;
        close_time?: string | null;
      }>
    >
  >({});
  const [selectedRunByCoverage, setSelectedRunByCoverage] = React.useState<
    Record<string, string | null>
  >({});
  const [reviewsByCoverage, setReviewsByCoverage] = React.useState<
    Record<string, CrashingOutPendingReview[]>
  >({});
  const [reviewFormDataByApproval, setReviewFormDataByApproval] = React.useState<
    Record<string, Record<string, unknown>>
  >({});
  const [submittingReviewId, setSubmittingReviewId] = React.useState<string | null>(null);
  const [progressMessageIndex, setProgressMessageIndex] = React.useState(0);
  const [interruptMessageByCoverage, setInterruptMessageByCoverage] = React.useState<
    Record<string, string>
  >({});

  const [startCrashingOut, { isLoading: isStarting }] = useStartCrashingOutMutation();
  const [cancelCrashingOut, { isLoading: isCancelling }] = useCancelCrashingOutMutation();
  const [updateCoverage] = useUpdateCoverageMutation();
  const [fetchStatus] = useLazyGetCrashingOutStatusQuery();
  const [fetchHistory] = useLazyGetCrashingOutHistoryQuery();
  const [fetchRunStatus] = useLazyGetCrashingOutRunStatusQuery();
  const [fetchPendingReviews] = useLazyGetCrashingOutPendingReviewsQuery();

  const loadCoverageData = React.useCallback(
    async (coverageId: string) => {
      try {
        const [history, pendingReviews] = await Promise.all([
          fetchHistory({ matterId, coverageId }).unwrap(),
          fetchPendingReviews({ matterId, coverageId }).unwrap(),
        ]);

        setHistoryByCoverage((prev) => ({ ...prev, [coverageId]: history.executions }));
        setReviewsByCoverage((prev) => ({ ...prev, [coverageId]: pendingReviews.reviews }));
        setReviewFormDataByApproval((prev) => {
          const next = { ...prev };
          pendingReviews.reviews.forEach((review) => {
            if (!next[review.approval_id]) {
              next[review.approval_id] = getDefaultFormData(review.response_schema);
            }
          });
          return next;
        });

        const selectedRunId = selectedRunByCoverage[coverageId];
        const hasSelectedRunInHistory = Boolean(
          selectedRunId &&
            history.executions.some((execution) => execution.run_id === selectedRunId),
        );

        if (hasSelectedRunInHistory && selectedRunId) {
          const selectedRunStatus = await fetchRunStatus({
            matterId,
            coverageId,
            runId: selectedRunId,
          }).unwrap();
          setStatusByCoverage((prev) => ({ ...prev, [coverageId]: selectedRunStatus }));
          setSelectedRunByCoverage((prev) => ({ ...prev, [coverageId]: selectedRunId }));
          return;
        }

        const running = history.executions.find((execution) => execution.status === 'running');
        if (running) {
          const runningStatus = await fetchStatus({ matterId, coverageId }).unwrap();
          const hasRunningTraces =
            Array.isArray(runningStatus.traces) && runningStatus.traces.length > 0;
          const runningLooksBroken =
            runningStatus.workflow_status === 'error' && !hasRunningTraces;

          if (runningLooksBroken) {
            const latestClosedRun = history.executions.find(
              (execution) => execution.run_id !== running.run_id,
            );
            if (latestClosedRun) {
              const fallbackRunStatus = await fetchRunStatus({
                matterId,
                coverageId,
                runId: latestClosedRun.run_id,
              }).unwrap();
              setStatusByCoverage((prev) => ({ ...prev, [coverageId]: fallbackRunStatus }));
              setSelectedRunByCoverage((prev) => ({
                ...prev,
                [coverageId]: latestClosedRun.run_id,
              }));
              return;
            }
          }

          setStatusByCoverage((prev) => ({ ...prev, [coverageId]: runningStatus }));
          setSelectedRunByCoverage((prev) => ({ ...prev, [coverageId]: running.run_id }));
          return;
        }

        const fallbackRunId = selectedRunId || history.executions[0]?.run_id;
        if (fallbackRunId) {
          const runStatus = await fetchRunStatus({
            matterId,
            coverageId,
            runId: fallbackRunId,
          }).unwrap();
          setStatusByCoverage((prev) => ({ ...prev, [coverageId]: runStatus }));
          setSelectedRunByCoverage((prev) => ({ ...prev, [coverageId]: fallbackRunId }));
        } else {
          setStatusByCoverage((prev) => ({ ...prev, [coverageId]: null }));
          setSelectedRunByCoverage((prev) => ({ ...prev, [coverageId]: null }));
        }
      } catch {
        toast.error('Failed to load Crashing Out history');
      }
    },
    [fetchHistory, fetchPendingReviews, fetchRunStatus, fetchStatus, matterId, selectedRunByCoverage],
  );

  React.useEffect(() => {
    if (!expandedCoverageId) return undefined;
    const status = statusByCoverage[expandedCoverageId]?.workflow_status;
    if (status !== 'running') return undefined;

    const intervalId = window.setInterval(() => {
      loadCoverageData(expandedCoverageId).catch(() => undefined);
    }, 500);
    return () => window.clearInterval(intervalId);
  }, [expandedCoverageId, loadCoverageData, statusByCoverage]);

  React.useEffect(() => {
    if (!expandedCoverageId) return undefined;
    const status = statusByCoverage[expandedCoverageId]?.workflow_status;
    if (status !== 'running') return undefined;

    const intervalId = window.setInterval(() => {
      setProgressMessageIndex((prev) => (prev + 1) % CRASHING_OUT_PROGRESS_MESSAGES.length);
    }, 1100);

    return () => window.clearInterval(intervalId);
  }, [expandedCoverageId, statusByCoverage]);

  const handleReviewFieldChange = React.useCallback(
    (approvalId: string, field: string, value: unknown) => {
      setReviewFormDataByApproval((prev) => ({
        ...prev,
        [approvalId]: {
          ...(prev[approvalId] || {}),
          [field]: value,
        },
      }));
    },
    [],
  );

  const handleSubmitReview = React.useCallback(
    async (coverageId: string, approvalId: string) => {
      try {
        setSubmittingReviewId(approvalId);
        await axiosInstance.post(`/api/agentflow/eval/${matterId}/${approvalId}/submit/`, {
          action: 'submit',
          modified_data: reviewFormDataByApproval[approvalId] || {},
        });
        toast.success('Review submitted');
        await loadCoverageData(coverageId);
      } catch {
        toast.error('Failed to submit review');
      } finally {
        setSubmittingReviewId(null);
      }
    },
    [loadCoverageData, matterId, reviewFormDataByApproval],
  );

  if (eligibleCoverages.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-zinc-600">
          No coverages available for Crashing Out runs.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {groupedCoverages.map((group) => (
        <div key={group.key} className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-700">{group.label}</h3>
            <Badge className="bg-zinc-100 text-zinc-700">{group.coverages.length}</Badge>
          </div>

          {group.coverages.map((coverage) => {
            const coverageId = coverage.id;
            const status = statusByCoverage[coverageId];
            const traces = (status?.traces || []) as unknown as StepTrace[];
            const runHistory = historyByCoverage[coverageId] || [];
            const hasRunningExecution = runHistory.some((run) => run.status === 'running');
            const selectedRunId = selectedRunByCoverage[coverageId];
            const isExpanded = expandedCoverageId === coverageId;
            const pendingReviews = reviewsByCoverage[coverageId] || [];

            return (
              <div key={coverageId} className="space-y-3">
                <CoverageCardRedesign
                  coverage={coverage}
                  matterId={matterId}
                  readOnly
                  forceShowProgressStepper
                />

                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-end gap-2">
                      {coverage.status !== CoverageStatus.APPROVED && (
                        <>
                          <Badge className="bg-amber-100 text-amber-700">{coverage.status}</Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                await updateCoverage({
                                  matterId,
                                  coverageId,
                                  coverage: { status: CoverageStatus.APPROVED },
                                }).unwrap();
                                toast.success('Coverage approved');
                              } catch {
                                toast.error('Failed to approve coverage');
                              }
                            }}
                          >
                            Approve
                          </Button>
                        </>
                      )}
                      {status?.workflow_status && (
                        <Badge className={statusColor(status.workflow_status)}>
                          {status.workflow_status}
                        </Badge>
                      )}
                      {hasRunningExecution && (
                        <>
                          <Input
                            value={interruptMessageByCoverage[coverageId] || ''}
                            onChange={(event) =>
                              setInterruptMessageByCoverage((prev) => ({
                                ...prev,
                                [coverageId]: event.target.value,
                              }))
                            }
                            placeholder="Interrupt message..."
                            className="h-8 w-56"
                          />
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={async () => {
                              const trimmedMessage = (
                                interruptMessageByCoverage[coverageId] || ''
                              ).trim();
                              if (!trimmedMessage) {
                                toast.error('Interrupt message is required');
                                return;
                              }
                              try {
                                const response = await cancelCrashingOut({
                                  matterId,
                                  coverageId,
                                  request: {
                                    reason: `Interrupted by operator: ${trimmedMessage}`,
                                  },
                                }).unwrap();
                                toast.success(response.message || 'Crashing out interrupted');
                                await loadCoverageData(coverageId);
                              } catch {
                                toast.error('Failed to interrupt Crashing Out');
                              }
                            }}
                            disabled={isCancelling}
                          >
                            <Square className="h-3.5 w-3.5 mr-1" />
                            Terminate
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await startCrashingOut({
                              matterId,
                              coverageId,
                              request: {},
                            }).unwrap();
                            toast.success(response.message || 'Crashing out');
                            await loadCoverageData(coverageId);
                            setExpandedCoverageId(coverageId);
                          } catch {
                            toast.error('Failed to start Crashing Out');
                          }
                        }}
                        disabled={isStarting}
                      >
                        <Play className="h-3.5 w-3.5 mr-1" />
                        Run
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => loadCoverageData(coverageId)}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const nextExpanded = isExpanded ? null : coverageId;
                          setExpandedCoverageId(nextExpanded);
                          if (nextExpanded) {
                            loadCoverageData(nextExpanded).catch(() => undefined);
                          }
                        }}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>

                  {isExpanded && (
                    <CardContent className="space-y-4">
                      {status?.workflow_status === 'running' && (
                        <div className="rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2">
                          <div className="flex items-center gap-3">
                            <img
                              src={CRASHING_OUT_PRIMARY_GIF}
                              alt="Crashing out in progress"
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = CRASHING_OUT_FALLBACK_GIF;
                              }}
                              className="h-10 w-10 rounded object-cover"
                            />
                            <div>
                              <div className="text-xs font-semibold text-blue-800">
                                {CRASHING_OUT_PROGRESS_MESSAGES[progressMessageIndex]}...
                              </div>
                              <div className="text-[11px] text-blue-700">
                                Agent is running tools and updating tasks live.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-zinc-500" />
                        <span className="text-xs font-medium text-zinc-600 uppercase tracking-wide">
                          Run History
                        </span>
                      </div>

                      {runHistory.length > 0 && (
                        <div className="max-w-sm">
                          <Select
                            value={selectedRunId || undefined}
                            onValueChange={async (runId) => {
                              setSelectedRunByCoverage((prev) => ({
                                ...prev,
                                [coverageId]: runId,
                              }));
                              try {
                                const runStatus = await fetchRunStatus({
                                  matterId,
                                  coverageId,
                                  runId,
                                }).unwrap();
                                setStatusByCoverage((prev) => ({
                                  ...prev,
                                  [coverageId]: runStatus,
                                }));
                              } catch {
                                toast.error('Failed to load selected run');
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a run" />
                            </SelectTrigger>
                            <SelectContent>
                              {runHistory.map((run) => (
                                <SelectItem key={run.run_id} value={run.run_id}>
                                  {run.status} · {formatRunStartTime(run)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {traces.length > 0 ? (
                        <CrashingOutExecutionHistory
                          traces={traces}
                          runStatus={status?.workflow_status}
                          matterId={matterId}
                          legacyTemporalStyle
                          pendingReviews={pendingReviews}
                          reviewFormDataByApproval={reviewFormDataByApproval}
                          submittingReviewId={submittingReviewId}
                          onReviewFieldChange={handleReviewFieldChange}
                          onSubmitReview={(approvalId) =>
                            handleSubmitReview(coverageId, approvalId)
                          }
                        />
                      ) : (
                        <div className="text-sm text-zinc-500">
                          No execution trace available yet.
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
